import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/UI/Button';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import { useToast } from '../../components/UI/ToastProvider';
import { planService } from '../../services/planService';
import { subscriptionService } from '../../services/subscriptionService';
import type { Plan } from '../../types/plan';
import {
  SUBSCRIPTION_STATUS_LABELS,
  SubscriptionStatus,
} from '../../types/licensingEnums';
import type { Subscription } from '../../types/subscription';

interface CustomerSubscriptionPanelProps {
  customerId: string;
  customerIsOwner?: boolean;
  onSubscriptionChanged?: () => void;
}

const OWNER_SUBSCRIPTION_READONLY_TOOLTIP =
  'Tenant owner (Baruk): o licenciamento é o catálogo completo e não depende de assinatura comercial.';

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  const response = (error as { response?: { data?: { message?: string; error?: string; title?: string } } })
    ?.response?.data;
  if (response?.error)
  {
    return response.error;
  }
  if (response?.message)
  {
    return response.message;
  }
  if (response?.title)
  {
    return response.title;
  }
  return fallback;
};

const formatDateTime = (value?: string | null): string => {
  if (!value)
  {
    return '—';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime()))
  {
    return value;
  }

  return date.toLocaleString('pt-BR');
};

const normalizeStatus = (status: string): SubscriptionStatus | string => {
  const match = Object.values(SubscriptionStatus).find(
    (item) => item.toLowerCase() === status.toLowerCase()
  );
  return match ?? status;
};

const isTerminalStatus = (status: string): boolean => {
  const normalized = normalizeStatus(status);
  return (
    normalized === SubscriptionStatus.Cancelled
    || normalized === SubscriptionStatus.Expired
  );
};

const canManageLifecycle = (subscription: Subscription | null): boolean => {
  if (!subscription)
  {
    return false;
  }

  return !isTerminalStatus(subscription.status);
};

export const CustomerSubscriptionPanel: React.FC<CustomerSubscriptionPanelProps> = ({
  customerId,
  customerIsOwner = false,
  onSubscriptionChanged,
}) => {
  const { addToast } = useToast();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [changePlanId, setChangePlanId] = useState('');
  const [creating, setCreating] = useState(false);
  const [activating, setActivating] = useState(false);
  const [changingPlan, setChangingPlan] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showChangePlanModal, setShowChangePlanModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const activePlans = useMemo(
    () => plans.filter((plan) => plan.active).sort((a, b) => a.sortOrder - b.sortOrder),
    [plans]
  );

  const planById = useMemo(() => {
    const map = new Map<string, Plan>();
    for (const plan of plans)
    {
      map.set(plan.id, plan);
    }
    return map;
  }, [plans]);

  const currentPlan = subscription ? planById.get(subscription.planId) : undefined;
  const statusKey = subscription ? normalizeStatus(subscription.status) : null;
  const statusLabel =
    statusKey && statusKey in SUBSCRIPTION_STATUS_LABELS
      ? SUBSCRIPTION_STATUS_LABELS[statusKey as SubscriptionStatus]
      : subscription?.status ?? '—';

  const showCreateForm =
    !subscription
    || isTerminalStatus(subscription.status);

  const showActivate =
    subscription != null
    && (
      normalizeStatus(subscription.status) === SubscriptionStatus.Pending
      || normalizeStatus(subscription.status) === SubscriptionStatus.Suspended
    );

  const showActiveActions =
    subscription != null
    && normalizeStatus(subscription.status) === SubscriptionStatus.Active
    && subscription.isActive;

  const loadData = useCallback(async () => {
    setLoading(true);
    try
    {
      const [plansData, subscriptionData] = await Promise.all([
        planService.getAll(),
        subscriptionService.getByCustomer(customerId),
      ]);
      setPlans(plansData);
      setSubscription(subscriptionData);

      const defaultPlanId = subscriptionData?.planId
        ?? plansData.find((plan) => plan.active)?.id
        ?? '';
      setSelectedPlanId(defaultPlanId);
      setChangePlanId(defaultPlanId);
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao carregar assinatura do cliente.'), 'error');
    }
    finally
    {
      setLoading(false);
    }
  }, [addToast, customerId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const notifyChanged = async () => {
    await loadData();
    onSubscriptionChanged?.();
  };

  const handleCreate = async () => {
    if (!selectedPlanId)
    {
      addToast('Selecione um plano.', 'error');
      return;
    }

    setCreating(true);
    try
    {
      await subscriptionService.createForCustomer(customerId, {
        planId: selectedPlanId,
        isActive: false,
      });
      addToast('Assinatura criada. Ative quando estiver pronto.', 'success');
      await notifyChanged();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao criar assinatura.'), 'error');
    }
    finally
    {
      setCreating(false);
    }
  };

  const handleActivate = async () => {
    if (!subscription)
    {
      return;
    }

    setActivating(true);
    try
    {
      await subscriptionService.activateForCustomer(customerId, subscription.id);
      addToast('Assinatura ativada.', 'success');
      await notifyChanged();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao ativar assinatura.'), 'error');
    }
    finally
    {
      setActivating(false);
    }
  };

  const handleChangePlan = async () => {
    if (!subscription || !changePlanId)
    {
      return;
    }

    if (changePlanId === subscription.planId)
    {
      addToast('Selecione um plano diferente do atual.', 'error');
      return;
    }

    setChangingPlan(true);
    try
    {
      await subscriptionService.changePlanForCustomer(customerId, subscription.id, {
        newPlanId: changePlanId,
      });
      addToast('Plano alterado com sucesso.', 'success');
      setShowChangePlanModal(false);
      await notifyChanged();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao trocar plano.'), 'error');
    }
    finally
    {
      setChangingPlan(false);
    }
  };

  const handleCancel = async () => {
    if (!subscription)
    {
      return;
    }

    setCancelling(true);
    try
    {
      await subscriptionService.cancelForCustomer(customerId, subscription.id, {
        cancellationReason: cancelReason.trim() || null,
      });
      addToast('Assinatura cancelada.', 'success');
      setShowCancelModal(false);
      setCancelReason('');
      await notifyChanged();
    }
    catch (error)
    {
      addToast(getApiErrorMessage(error, 'Erro ao cancelar assinatura.'), 'error');
    }
    finally
    {
      setCancelling(false);
    }
  };

  if (loading)
  {
    return (
      <section className="border rounded-lg p-5 bg-white mb-8">
        <p className="text-gray-600">Carregando assinatura…</p>
      </section>
    );
  }

  if (customerIsOwner)
  {
    return (
      <section
        className="border rounded-lg p-5 bg-gray-50 mb-8 space-y-4"
        title={OWNER_SUBSCRIPTION_READONLY_TOOLTIP}
      >
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-700">Assinatura do cliente</h2>
            <p className="text-sm text-gray-600 mt-1">
              Indisponível para tenant owner. O licenciamento vigente usa o catálogo completo de
              módulos e features, sem assinatura comercial.
            </p>
          </div>
          <span
            className="text-gray-400 cursor-help shrink-0 text-lg leading-none"
            title={OWNER_SUBSCRIPTION_READONLY_TOOLTIP}
            aria-label={OWNER_SUBSCRIPTION_READONLY_TOOLTIP}
          >
            ⓘ
          </span>
        </div>

        {subscription && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm opacity-75">
            <div>
              <dt className="text-gray-500">Plano (somente leitura)</dt>
              <dd className="font-medium">
                {currentPlan ? `${currentPlan.name} (${currentPlan.code})` : subscription.planId}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd className="font-medium">{statusLabel}</dd>
            </div>
          </dl>
        )}
      </section>
    );
  }

  return (
    <>
      <section className="border rounded-lg p-5 bg-white mb-8 space-y-6">
        <div>
          <h2 className="text-lg font-semibold">Assinatura do cliente</h2>
          <p className="text-sm text-gray-500 mt-1">
            Crie, ative, troque o plano ou cancele a assinatura vinculada à conta do cliente.
          </p>
        </div>

        {subscription && canManageLifecycle(subscription) && (
          <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Plano</dt>
              <dd className="font-medium">
                {currentPlan ? `${currentPlan.name} (${currentPlan.code})` : subscription.planId}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Status</dt>
              <dd className="font-medium">{statusLabel}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Principal</dt>
              <dd className="font-medium">{subscription.isPrimary ? 'Sim' : 'Não'}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Início</dt>
              <dd>{formatDateTime(subscription.startDate)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Fim do trial</dt>
              <dd>{formatDateTime(subscription.trialEndsAt)}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Cancelada em</dt>
              <dd>{formatDateTime(subscription.cancelledAt)}</dd>
            </div>
            {subscription.cancellationReason && (
              <div className="sm:col-span-2 lg:col-span-3">
                <dt className="text-gray-500">Motivo do cancelamento</dt>
                <dd>{subscription.cancellationReason}</dd>
              </div>
            )}
          </dl>
        )}

        {subscription && isTerminalStatus(subscription.status) && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            A assinatura está {statusLabel.toLowerCase()}. Crie uma nova assinatura para reativar o
            licenciamento.
          </p>
        )}

        {showCreateForm && (
          <div className="border-t pt-4 space-y-4">
            <h3 className="font-medium">
              {subscription ? 'Nova assinatura' : 'Criar assinatura'}
            </h3>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Plano</label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">Selecione…</option>
                  {activePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.code})
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="button"
                variant="primary"
                disabled={creating || !selectedPlanId}
                onClick={() => void handleCreate()}
              >
                {creating ? 'Criando…' : 'Criar assinatura'}
              </Button>
            </div>
          </div>
        )}

        {showActivate && (
          <div className="flex justify-end border-t pt-4">
            <Button
              type="button"
              variant="primary"
              disabled={activating}
              onClick={() => void handleActivate()}
            >
              {activating ? 'Ativando…' : 'Ativar assinatura'}
            </Button>
          </div>
        )}

        {showActiveActions && (
          <div className="border-t pt-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label className="block text-sm font-medium mb-1">Novo plano</label>
                <select
                  value={changePlanId}
                  onChange={(e) => setChangePlanId(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                >
                  {activePlans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} ({plan.code})
                    </option>
                  ))}
                </select>
              </div>
              <Button
                type="button"
                variant="secondary"
                disabled={!changePlanId || changePlanId === subscription.planId}
                onClick={() => setShowChangePlanModal(true)}
              >
                Trocar plano
              </Button>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Motivo do cancelamento (opcional)</label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Ex.: solicitação do cliente"
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="danger"
                onClick={() => setShowCancelModal(true)}
              >
                Cancelar assinatura
              </Button>
            </div>
          </div>
        )}
      </section>

      <ConfirmModal
        isOpen={showChangePlanModal}
        title="Confirmar troca de plano"
        message={`Deseja alterar o plano para "${
          planById.get(changePlanId)?.name ?? changePlanId
        }"? Downgrades podem remover módulos licenciados da experiência do cliente.`}
        confirmText={changingPlan ? 'Alterando…' : 'Confirmar'}
        onConfirm={() => void handleChangePlan()}
        onCancel={() => setShowChangePlanModal(false)}
      />

      <ConfirmModal
        isOpen={showCancelModal}
        title="Cancelar assinatura"
        message="A assinatura será encerrada. O cliente deixará de ter licenciamento ativo até uma nova assinatura ser criada."
        confirmText={cancelling ? 'Cancelando…' : 'Confirmar cancelamento'}
        onConfirm={() => void handleCancel()}
        onCancel={() => setShowCancelModal(false)}
      />
    </>
  );
};
